import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Slider,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';
import {
  AutoAwesome as AutoAwesomeIcon,
  BugReport as BugReportIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  OpenInFull as OpenInFullIcon,
  PriorityHigh as PriorityHighIcon,
  Radar as RadarIcon,
  Search as SearchIcon,
  Shield as ShieldIcon,
  Terminal as TerminalIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, useParams } from 'react-router-dom';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';

const VERIFY_THRESHOLD = 25;
const METHOD_ORDER = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeMethod = (value, fallback = 'GET') => {
  const method = String(value || fallback || 'GET').trim().toUpperCase();
  return METHOD_ORDER.includes(method) ? method : fallback;
};

const stripQueryString = (value) => String(value || '').split('?')[0].trim();

const extractQueryKeysFromPath = (value) => {
  const raw = String(value || '').trim();
  if (!raw.includes('?')) return [];
  try {
    const candidate = raw.startsWith('http') ? raw : `https://example.test${raw.startsWith('/') ? '' : '/'}${raw}`;
    const parsed = new URL(candidate);
    return [...new Set(Array.from(parsed.searchParams.keys()).map((item) => item.trim()).filter(Boolean))];
  } catch (error) {
    const queryString = raw.split('?')[1] || '';
    return [...new Set(Array.from(new URLSearchParams(queryString).keys()).map((item) => item.trim()).filter(Boolean))];
  }
};

const extractRouteParamKeys = (value) =>
  [...String(value || '').matchAll(/[:{]([A-Za-z0-9_-]+)[}]?/g)]
    .map((match) => String(match[1] || '').trim())
    .filter(Boolean);

const scoreColor = (score) => (score >= 75 ? '#d32f2f' : score >= 50 ? '#ef6c00' : PRIMARYCOLOR);
const scoreLabel = (score) => (score >= 75 ? 'Likely' : score >= 50 ? 'Possible' : 'Low');
const willVerify = (score) => num(score) >= VERIFY_THRESHOLD;
const trunc = (value, length = 46) => (String(value || '').length > length ? `${String(value).slice(0, length - 3)}...` : String(value || ''));
const methodColor = (method) =>
  ({
    GET: '#DCEEFF',
    POST: '#E3F4E8',
    PUT: '#FFF3D5',
    PATCH: '#F2E6FF',
    DELETE: '#FFE3DE',
  }[normalizeMethod(method, 'GET')] || '#E7EDF6');

const methodTextColor = (method) =>
  ({
    GET: '#0B5CAD',
    POST: '#1C7C3F',
    PUT: '#9A5B00',
    PATCH: '#7A2CB5',
    DELETE: '#C0392B',
  }[normalizeMethod(method, 'GET')] || '#27415E');

const isStateChangingMethod = (method) => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(normalizeMethod(method, 'GET'));

const getFindings = (attack) => (Array.isArray(attack?.exploit_results?.findings) ? attack.exploit_results.findings : []);

const sevKey = (severity) => {
  const value = String(severity || '').toLowerCase();
  if (value.includes('critical')) return 'critical';
  if (value.includes('high')) return 'high';
  if (value.includes('medium')) return 'medium';
  if (value.includes('low')) return 'low';
  if (value.includes('info')) return 'info';
  return 'info';
};

const sevColor = (key) =>
  ({
    critical: '#B71C1C',
    high: '#D32F2F',
    medium: '#EF6C00',
    low: '#1976D2',
    info: '#455A64',
  }[key] || '#455A64');

const attackIcon = (name = '') => {
  const value = String(name || '').toLowerCase();
  if (value.includes('sql')) return <BugReportIcon fontSize="small" />;
  if (value.includes('auth') || value.includes('jwt') || value.includes('session')) return <ShieldIcon fontSize="small" />;
  if (value.includes('ssrf') || value.includes('request forgery')) return <RadarIcon fontSize="small" />;
  if (value.includes('rce') || value.includes('command') || value.includes('os command')) return <PriorityHighIcon fontSize="small" />;
  return <WarningAmberIcon fontSize="small" />;
};

const mdComponents = {
  h1: (props) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 900 }} {...props} />,
  h2: (props) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 900 }} {...props} />,
  p: (props) => <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.7 }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 16 }}>
      <Typography component="span" variant="body2" {...props} />
    </li>
  ),
  em: (props) => (
    <Typography component="em" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }} {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code
        style={{
          background: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 6,
          padding: '1px 6px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
        }}
        {...props}
      />
    ) : (
      <Box
        component="code"
        sx={{
          display: 'block',
          bgcolor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto',
        }}
        {...props}
      />
    ),
};

const mdComponentsTerminal = {
  h1: (props) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 900, color: 'inherit' }} {...props} />,
  h2: (props) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 900, color: 'inherit' }} {...props} />,
  p: (props) => <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.7, color: 'inherit' }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 16 }}>
      <Typography component="span" variant="body2" sx={{ color: 'inherit' }} {...props} />
    </li>
  ),
  em: (props) => (
    <Typography component="em" variant="body2" sx={{ fontStyle: 'italic', color: alpha('#E8F0FF', 0.75) }} {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6,
          padding: '1px 6px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
          color: '#E8F0FF',
        }}
        {...props}
      />
    ) : (
      <Box
        component="code"
        sx={{
          display: 'block',
          bgcolor: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto',
          color: '#E8F0FF',
        }}
        {...props}
      />
    ),
};

const PentestWebThreatModel = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [search, setSearch] = useState('');
  const [kbdIndex, setKbdIndex] = useState(0);
  const [attackSearch, setAttackSearch] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [sortMode, setSortMode] = useState('SCORE');
  const [assetSort, setAssetSort] = useState('RISK');
  const [methodFilter, setMethodFilter] = useState('ALL');
  const [tab, setTab] = useState(0);
  const [rawOpen, setRawOpen] = useState(false);
  const [selectedAttack, setSelectedAttack] = useState(null);
  const [showLowConfidence, setShowLowConfidence] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${BASEURL}/pentests/web/threat_model?pentest_id=${pentest_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) throw new Error(data.message || 'Load error');

        const overviewModel = {
          id: 0,
          method: 'OVERVIEW',
          path: 'Web Overview',
          displayPath: 'Web Overview',
          attacks: [],
          md: data?.threat_model?.api || '',
        };

        const endpointModels = (data?.threat_model?.endpoints || []).map((endpoint, index) => ({
          id: index + 1,
          method: normalizeMethod(endpoint?.http_method, 'GET'),
          path: endpoint?.path || `Endpoint ${index + 1}`,
          displayPath: stripQueryString(endpoint?.path || `Endpoint ${index + 1}`),
          attacks: Array.isArray(endpoint?.threat_model?.attacks) ? endpoint.threat_model.attacks : [],
          md: endpoint?.threat_model?.threat_model || '',
        }));

        setEndpoints([overviewModel, ...endpointModels]);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        navigate('/');
      });
  }, [navigate, pentest_id]);

  useEffect(() => {
    setTab(0);
    setAttackSearch('');
    setMinScore(0);
    setSortMode('SCORE');
    setSelectedAttack(null);
    setRawOpen(false);
    setShowLowConfidence(false);
  }, [activeIdx]);

  const active = useMemo(() => endpoints[activeIdx] || null, [endpoints, activeIdx]);

  const endpointMeta = useMemo(() => {
    const meta = {};

    endpoints.slice(1).forEach((endpoint) => {
      const attacks = Array.isArray(endpoint?.attacks) ? endpoint.attacks : [];
      const scores = attacks.map((attack) => num(attack?.score));
      const maxScore = scores.length ? Math.max(...scores) : 0;
      const likelyCount = attacks.filter((attack) => num(attack?.score) >= 75).length;
      const queuedCount = attacks.filter((attack) => willVerify(attack?.score)).length;

      const queryKeys = extractQueryKeysFromPath(endpoint?.path);
      const pathParams = extractRouteParamKeys(endpoint?.path);
      const inputCount = queryKeys.length + pathParams.length;

      const severity = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
      let findingsCount = 0;

      attacks.forEach((attack) => {
        const findings = getFindings(attack);
        findingsCount += findings.length;
        findings.forEach((finding) => {
          severity[sevKey(finding?.severity)] += 1;
        });
      });

      meta[endpoint.path] = {
        method: endpoint.method,
        displayPath: endpoint.displayPath,
        maxScore,
        likelyCount,
        queuedCount,
        totalAttacks: attacks.length,
        findingsCount,
        severity,
        queryKeys,
        pathParams,
        inputCount,
        stateChanging: isStateChangingMethod(endpoint.method),
      };
    });

    return meta;
  }, [endpoints]);

  const overviewStats = useMemo(() => {
    const endpointRows = endpoints.slice(1);
    const uniqueAttacks = new Set();
    const activeMethods = new Set();
    const scoreBins = { low: 0, mid: 0, high: 0, vhigh: 0 };
    const severity = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };

    let totalAttacks = 0;
    let totalFindings = 0;
    let endpointsWithFindings = 0;
    let endpointsWithLikelyPaths = 0;
    let endpointsWithInputs = 0;
    let stateChangingRoutes = 0;

    endpointRows.forEach((endpoint) => {
      const meta = endpointMeta[endpoint.path] || {};
      const attacks = Array.isArray(endpoint?.attacks) ? endpoint.attacks : [];

      if (meta.findingsCount > 0) endpointsWithFindings += 1;
      if (meta.likelyCount > 0) endpointsWithLikelyPaths += 1;
      if (meta.inputCount > 0) endpointsWithInputs += 1;
      if (meta.stateChanging) stateChangingRoutes += 1;

      activeMethods.add(endpoint.method);

      attacks.forEach((attack) => {
        totalAttacks += 1;
        uniqueAttacks.add(attack?.name || 'Unknown attack');

        const score = num(attack?.score);
        if (score >= 75) scoreBins.vhigh += 1;
        else if (score >= 50) scoreBins.high += 1;
        else if (score >= 25) scoreBins.mid += 1;
        else scoreBins.low += 1;

        const findings = getFindings(attack);
        totalFindings += findings.length;
        findings.forEach((finding) => {
          severity[sevKey(finding?.severity)] += 1;
        });
      });
    });

    return {
      totalEndpoints: endpointRows.length,
      totalAttacks,
      uniqueAttacks: uniqueAttacks.size,
      totalFindings,
      endpointsWithFindings,
      endpointsWithLikelyPaths,
      endpointsWithInputs,
      stateChangingRoutes,
      activeMethods: activeMethods.size,
      scoreBins,
      severity,
    };
  }, [endpoints, endpointMeta]);

  const filteredEndpoints = useMemo(() => {
    const query = search.trim().toLowerCase();
    const overview = endpoints.find((endpoint) => endpoint.id === 0);

    const rankedEndpoints = endpoints
      .filter((endpoint) => endpoint.id !== 0)
      .filter((endpoint) => {
        if (methodFilter !== 'ALL' && endpoint.method !== methodFilter) return false;
        if (!query) return true;

        const meta = endpointMeta[endpoint.path] || {};
        return [
          endpoint.path,
          endpoint.displayPath,
          endpoint.method,
          meta.queryKeys?.join(' '),
          meta.pathParams?.join(' '),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query);
      })
      .sort((left, right) => {
        if (assetSort === 'NAME') {
          return `${left.method} ${left.displayPath}`.localeCompare(`${right.method} ${right.displayPath}`);
        }

        const leftMeta = endpointMeta[left.path] || {};
        const rightMeta = endpointMeta[right.path] || {};

        const leftPriority = (leftMeta.findingsCount > 0 ? 4 : 0) + (leftMeta.likelyCount > 0 ? 2 : 0) + (leftMeta.maxScore >= 50 ? 1 : 0);
        const rightPriority = (rightMeta.findingsCount > 0 ? 4 : 0) + (rightMeta.likelyCount > 0 ? 2 : 0) + (rightMeta.maxScore >= 50 ? 1 : 0);

        return (
          rightPriority - leftPriority ||
          num(rightMeta.maxScore) - num(leftMeta.maxScore) ||
          num(rightMeta.findingsCount) - num(leftMeta.findingsCount) ||
          `${left.method} ${left.displayPath}`.localeCompare(`${right.method} ${right.displayPath}`)
        );
      });

    return overview ? [overview, ...rankedEndpoints] : rankedEndpoints;
  }, [assetSort, endpointMeta, endpoints, methodFilter, search]);

  const onKeyDownList = (event) => {
    if (!filteredEndpoints.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setKbdIndex((previous) => Math.min(previous + 1, filteredEndpoints.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setKbdIndex((previous) => Math.max(previous - 1, 0));
    } else if (event.key === 'Enter') {
      const selected = filteredEndpoints[kbdIndex];
      if (!selected) return;
      const nextIndex = endpoints.findIndex((endpoint) => endpoint.id === selected.id);
      if (nextIndex > -1) setActiveIdx(nextIndex);
    }
  };

  const goToEndpointPath = (path, method) => {
    const nextIndex = endpoints.findIndex((endpoint) => endpoint.path === path && endpoint.method === method);
    if (nextIndex > -1) setActiveIdx(nextIndex);
  };

  const attacksForUI = useMemo(() => {
    if (activeIdx === 0) return [];

    const query = attackSearch.trim().toLowerCase();
    const list = (active?.attacks || [])
      .filter((attack) => num(attack?.score) >= minScore)
      .filter((attack) => {
        if (!query) return true;
        return [attack?.name, attack?.reason].filter(Boolean).join(' ').toLowerCase().includes(query);
      });

    list.sort((left, right) => {
      if (sortMode === 'NAME') return String(left?.name || '').localeCompare(String(right?.name || ''));
      return num(right?.score) - num(left?.score);
    });

    return list;
  }, [active, activeIdx, attackSearch, minScore, sortMode]);

  const groupedAttacks = useMemo(() => {
    const groups = { likely: [], possible: [], low: [] };

    attacksForUI.forEach((attack) => {
      const score = num(attack?.score);
      if (score >= 75) groups.likely.push(attack);
      else if (score >= 25) groups.possible.push(attack);
      else groups.low.push(attack);
    });

    return groups;
  }, [attacksForUI]);

  const attackSections = useMemo(() => {
    const sections = [];

    if (groupedAttacks.likely.length) {
      sections.push({
        key: 'likely',
        title: 'Likely attack paths',
        helper: 'Highest-confidence hypotheses that deserve immediate analyst review.',
        rows: groupedAttacks.likely,
        color: '#D32F2F',
      });
    }

    if (groupedAttacks.possible.length) {
      sections.push({
        key: 'possible',
        title: 'Possible attack paths',
        helper: 'Plausible hypotheses above the verification threshold that should stay in active review.',
        rows: groupedAttacks.possible,
        color: '#EF6C00',
      });
    }

    if (groupedAttacks.low.length && showLowConfidence) {
      sections.push({
        key: 'low',
        title: 'Low-confidence attack paths',
        helper: 'Lower-confidence hypotheses that are still useful for analyst awareness.',
        rows: groupedAttacks.low,
        color: PRIMARYCOLOR,
      });
    }

    return sections;
  }, [groupedAttacks, showLowConfidence]);

  const assetOverviewRows = useMemo(
    () =>
      endpoints
        .slice(1)
        .map((endpoint) => {
          const meta = endpointMeta[endpoint.path] || {};
          return {
            id: endpoint.id,
            method: endpoint.method,
            path: endpoint.path,
            displayPath: endpoint.displayPath,
            maxScore: num(meta.maxScore),
            likelyCount: num(meta.likelyCount),
            queuedCount: num(meta.queuedCount),
            totalAttacks: num(meta.totalAttacks),
            findingsCount: num(meta.findingsCount),
            inputCount: num(meta.inputCount),
            queryCount: Array.isArray(meta.queryKeys) ? meta.queryKeys.length : 0,
            pathParamCount: Array.isArray(meta.pathParams) ? meta.pathParams.length : 0,
            stateChanging: Boolean(meta.stateChanging),
          };
        })
        .sort(
          (left, right) =>
            (right.findingsCount > 0) - (left.findingsCount > 0) ||
            right.maxScore - left.maxScore ||
            right.likelyCount - left.likelyCount ||
            `${left.method} ${left.displayPath}`.localeCompare(`${right.method} ${right.displayPath}`)
        ),
    [endpointMeta, endpoints]
  );

  const overviewLaunchRows = useMemo(
    () =>
      assetOverviewRows
        .filter((row) => row.findingsCount > 0 || row.likelyCount > 0 || row.maxScore >= VERIFY_THRESHOLD)
        .slice(0, 10),
    [assetOverviewRows]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <Nav />

      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                {activeIdx === 0 ? 'Web Application Threat Overview' : 'Endpoint Threat Review'}
              </Typography>
              {activeIdx !== 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Hypotheses, findings, and model narrative for endpoint:{' '}
                  <b>
                    {active?.method} {active?.displayPath || active?.path}
                  </b>
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
              <Button variant="contained" size="small" startIcon={<InfoOutlinedIcon />} onClick={() => setActiveIdx(0)}>
                Overview
              </Button>
            </Stack>
          </Stack>

          {activeIdx === 0 && (
            <Alert
              icon={<AutoAwesomeIcon />}
              severity="info"
              sx={{
                mb: 2,
                borderRadius: 3,
                bgcolor: alpha(PRIMARYCOLOR, 0.06),
                border: `1px solid ${alpha(PRIMARYCOLOR, 0.18)}`,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography sx={{ fontWeight: 950 }}>Threat paths are hypotheses, not confirmed vulnerabilities.</Typography>
              <Typography variant="body2" color="text.secondary">
                Scores of {VERIFY_THRESHOLD}+ are prioritized for validation, but a high score alone does not mean the endpoint is vulnerable.
              </Typography>
            </Alert>
          )}

          {activeIdx === 0 ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      TOTAL ENDPOINTS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.totalEndpoints}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Review the routes modeled across this web application
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ENDPOINTS WITH LIKELY PATHS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.endpointsWithLikelyPaths}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Routes with at least one high-confidence attack path
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ENDPOINTS WITH FINDINGS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.endpointsWithFindings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Routes with confirmed findings already recorded
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ACTIVE METHODS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.activeMethods}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {overviewStats.stateChangingRoutes} state-changing routes in scope
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              <Card
                sx={{
                  bgcolor: '#fff',
                  mb: 2,
                  borderRadius: 3,
                  boxShadow: 2,
                  border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ mb: 1.5 }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 950 }}>
                        Endpoints needing attention
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start here, then jump directly into the routes with likely paths or confirmed findings.
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${overviewLaunchRows.length || assetOverviewRows.slice(0, 10).length} endpoints shown`}
                      sx={{ fontWeight: 900, bgcolor: alpha(PRIMARYCOLOR, 0.08) }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    <Chip label={`Unique attack types: ${overviewStats.uniqueAttacks}`} sx={{ fontWeight: 900 }} />
                    <Chip label={`Endpoints with inputs: ${overviewStats.endpointsWithInputs}`} sx={{ fontWeight: 900 }} />
                    <Chip
                      label={`Confirmed findings: ${overviewStats.totalFindings}`}
                      sx={{
                        fontWeight: 900,
                        bgcolor: alpha(overviewStats.totalFindings > 0 ? '#D32F2F' : PRIMARYCOLOR, 0.08),
                        color: overviewStats.totalFindings > 0 ? '#D32F2F' : PRIMARYCOLOR,
                      }}
                    />
                  </Stack>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Endpoint</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Method</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Score</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Likely</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Findings</b>
                          </TableCell>
                          <TableCell>
                            <b>Input Surface</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(overviewLaunchRows.length ? overviewLaunchRows : assetOverviewRows.slice(0, 10)).map((row) => (
                          <TableRow key={`${row.method}-${row.path}`} hover sx={{ cursor: 'pointer' }} onClick={() => goToEndpointPath(row.path, row.method)}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 900, wordBreak: 'break-word' }}>
                                {row.displayPath || row.path}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={row.method}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: methodColor(row.method),
                                  color: methodTextColor(row.method),
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={`${Math.round(row.maxScore)}`}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: alpha(scoreColor(row.maxScore), 0.1),
                                  color: scoreColor(row.maxScore),
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">{row.likelyCount}</TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={row.findingsCount}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: alpha(row.findingsCount > 0 ? '#D32F2F' : PRIMARYCOLOR, 0.08),
                                  color: row.findingsCount > 0 ? '#D32F2F' : PRIMARYCOLOR,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {row.queryCount} query keys, {row.pathParamCount} path params
                                {row.stateChanging ? ' - state changing' : ''}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Alert
                severity="info"
                icon={<ChevronRightIcon />}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  bgcolor: alpha(PRIMARYCOLOR, 0.05),
                  border: `1px dashed ${alpha(PRIMARYCOLOR, 0.22)}`,
                }}
              >
                <Typography sx={{ fontWeight: 950 }}>Next step: open an endpoint</Typography>
                <Typography variant="body2" color="text.secondary">
                  Click a row above or choose an endpoint on the right to go straight into its ranked paths, model narrative, and confirmed findings.
                </Typography>
              </Alert>
            </>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ENDPOINT
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, mb: 0.75 }}>
                      <Chip
                        size="small"
                        label={active?.method}
                        sx={{
                          fontWeight: 900,
                          bgcolor: methodColor(active?.method),
                          color: methodTextColor(active?.method),
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 900, wordBreak: 'break-word' }}>
                      {active?.displayPath || active?.path}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      MAX SCORE
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950, color: scoreColor(endpointMeta[active?.path]?.maxScore || 0) }}>
                      {Math.round(endpointMeta[active?.path]?.maxScore || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Highest attack-path likelihood on this route
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ATTACK PATHS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {endpointMeta[active?.path]?.totalAttacks || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {endpointMeta[active?.path]?.likelyCount || 0} likely and {endpointMeta[active?.path]?.queuedCount || 0} queued for verification
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      CONFIRMED FINDINGS
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 950,
                        color: (endpointMeta[active?.path]?.findingsCount || 0) > 0 ? '#D32F2F' : 'inherit',
                      }}
                    >
                      {endpointMeta[active?.path]?.findingsCount || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {endpointMeta[active?.path]?.queryKeys?.length || 0} query keys and {endpointMeta[active?.path]?.pathParams?.length || 0} path params identified
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: 2,
                  border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack spacing={0.25}>
                      <Typography variant="h6" sx={{ fontWeight: 950 }}>
                        Endpoint attack review
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review ranked attack paths for{' '}
                        <b>
                          {active?.method} {active?.displayPath || active?.path}
                        </b>{' '}
                        and open any path for evidence and findings.
                      </Typography>

                      {(() => {
                        const meta = endpointMeta[active?.path] || {};
                        return (
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip size="small" label={`Attacks: ${meta.totalAttacks || 0}`} />
                            <Chip size="small" label={`Likely: ${meta.likelyCount || 0}`} sx={{ bgcolor: '#FFF3E0' }} />
                            <Chip size="small" label={`Inputs: ${meta.inputCount || 0}`} sx={{ bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
                            <Chip
                              size="small"
                              label={`Findings: ${meta.findingsCount || 0}`}
                              sx={{
                                bgcolor: alpha('#D32F2F', 0.08),
                                color: (meta.findingsCount || 0) > 0 ? '#D32F2F' : 'inherit',
                                fontWeight: 900,
                              }}
                            />
                          </Stack>
                        );
                      })()}
                    </Stack>

                    <Button variant="outlined" size="small" startIcon={<TerminalIcon />} onClick={() => setRawOpen(true)} sx={{ borderStyle: 'dashed' }}>
                      Fullscreen narrative
                    </Button>
                  </Stack>

                  <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    sx={{
                      minHeight: 38,
                      '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 },
                    }}
                  >
                    <Tab icon={<BugReportIcon />} iconPosition="start" label={`Attack Paths (${attacksForUI.length})`} />
                    <Tab icon={<TerminalIcon />} iconPosition="start" label="Model Narrative" />
                  </Tabs>
                </CardContent>

                <Divider />

                {tab === 0 && (
                  <CardContent>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          fullWidth
                          value={attackSearch}
                          onChange={(event) => setAttackSearch(event.target.value)}
                          placeholder="Search attack paths (name / evidence)..."
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                            Minimum likelihood score: {minScore}
                          </Typography>
                          <Slider size="small" value={minScore} onChange={(_, value) => setMinScore(value)} min={0} max={100} />
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <ToggleButtonGroup size="small" value={sortMode} exclusive onChange={(_, value) => value && setSortMode(value)} fullWidth>
                          <ToggleButton value="SCORE" sx={{ fontWeight: 900 }}>
                            Sort: Score
                          </ToggleButton>
                          <ToggleButton value="NAME" sx={{ fontWeight: 900 }}>
                            Sort: Name
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                      <Chip
                        size="small"
                        label={`Likely: ${groupedAttacks.likely.length}`}
                        sx={{ fontWeight: 900, bgcolor: alpha('#D32F2F', 0.08), color: '#D32F2F' }}
                      />
                      <Chip
                        size="small"
                        label={`Possible: ${groupedAttacks.possible.length}`}
                        sx={{ fontWeight: 900, bgcolor: alpha('#EF6C00', 0.08), color: '#EF6C00' }}
                      />
                      <Chip
                        size="small"
                        label={`Low confidence: ${groupedAttacks.low.length}`}
                        sx={{ fontWeight: 900, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }}
                      />
                    </Stack>

                    <Stack spacing={2}>
                      {attackSections.map((section) => (
                        <Box key={section.key}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 1 }}
                          >
                            <Stack spacing={0.25}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                {section.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {section.helper}
                              </Typography>
                            </Stack>
                            <Chip
                              size="small"
                              label={`${section.rows.length} path${section.rows.length === 1 ? '' : 's'}`}
                              sx={{
                                fontWeight: 900,
                                bgcolor: alpha(section.color, 0.08),
                                color: section.color,
                                border: `1px solid ${alpha(section.color, 0.16)}`,
                              }}
                            />
                          </Stack>

                          <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {section.rows.map((attack, index) => {
                              const score = num(attack?.score);
                              const findings = getFindings(attack);
                              const findingCount = findings.length;
                              const confirmed = attack?.exploit_results?.vulnerable === true || findingCount > 0;
                              const activeMeta = endpointMeta[active?.path] || {};

                              return (
                                <Paper
                                  key={`${section.key}-${index}-${attack?.name}`}
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    borderColor: alpha(section.color, 0.18),
                                    bgcolor: '#fff',
                                  }}
                                >
                                  <ListItemButton onClick={() => setSelectedAttack(attack)} sx={{ alignItems: 'flex-start', py: 1.25 }}>
                                    <Box sx={{ mt: 0.25, mr: 1, color: alpha('#000', 0.65) }}>{attackIcon(attack?.name)}</Box>

                                    <Box sx={{ flex: 1 }}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                          {attack?.name}
                                        </Typography>

                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                                          <Chip
                                            size="small"
                                            label={`${scoreLabel(score)} - ${score}`}
                                            sx={{
                                              fontWeight: 950,
                                              bgcolor: alpha(scoreColor(score), 0.12),
                                              color: scoreColor(score),
                                              border: `1px solid ${alpha(scoreColor(score), 0.24)}`,
                                            }}
                                          />
                                          <Chip
                                            size="small"
                                            variant={willVerify(score) ? 'filled' : 'outlined'}
                                            label={willVerify(score) ? 'Ready for verification' : 'Analyst watchlist'}
                                            sx={{
                                              fontWeight: 900,
                                              borderStyle: willVerify(score) ? 'solid' : 'dashed',
                                              bgcolor: willVerify(score) ? alpha('#EF6C00', 0.08) : 'transparent',
                                              color: willVerify(score) ? '#EF6C00' : PRIMARYCOLOR,
                                            }}
                                          />
                                          <Chip
                                            size="small"
                                            label={`Findings: ${findingCount}`}
                                            sx={{
                                              fontWeight: 900,
                                              bgcolor: alpha(confirmed ? '#D32F2F' : PRIMARYCOLOR, 0.08),
                                              color: confirmed ? '#D32F2F' : PRIMARYCOLOR,
                                              border: `1px solid ${alpha(confirmed ? '#D32F2F' : PRIMARYCOLOR, 0.14)}`,
                                            }}
                                          />
                                        </Stack>
                                      </Stack>

                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          mt: 0.5,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {attack?.reason}
                                      </Typography>

                                      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`${active?.method} ${trunc(active?.displayPath || active?.path, 52)}`}
                                          sx={{ maxWidth: '100%' }}
                                        />
                                        <Chip size="small" variant="outlined" label={`Query keys: ${activeMeta.queryKeys?.length || 0}`} />
                                        <Chip size="small" variant="outlined" label={`Path params: ${activeMeta.pathParams?.length || 0}`} />
                                        {activeMeta.stateChanging && <Chip size="small" variant="outlined" label="State-changing route" />}
                                      </Stack>

                                      <Box sx={{ mt: 1 }}>
                                        <Box sx={{ position: 'relative' }}>
                                          <LinearProgress
                                            variant="determinate"
                                            value={Math.max(0, Math.min(100, score))}
                                            sx={{ height: 8, borderRadius: 99, bgcolor: alpha(PRIMARYCOLOR, 0.08) }}
                                          />
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: -2,
                                              left: `${VERIFY_THRESHOLD}%`,
                                              transform: 'translateX(-1px)',
                                              width: 2,
                                              height: 12,
                                              bgcolor: alpha('#000', 0.35),
                                            }}
                                          />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          Likelihood score with verification threshold marker
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </ListItemButton>
                                </Paper>
                              );
                            })}
                          </List>
                        </Box>
                      ))}

                      {groupedAttacks.low.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowLowConfidence((previous) => !previous)}
                          sx={{ alignSelf: 'flex-start', borderStyle: 'dashed', fontWeight: 900 }}
                        >
                          {showLowConfidence
                            ? 'Hide low-confidence attack paths'
                            : `Show low-confidence attack paths (${groupedAttacks.low.length})`}
                        </Button>
                      )}

                      {attacksForUI.length === 0 && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderStyle: 'dashed', bgcolor: '#fff' }}>
                          <Typography sx={{ fontWeight: 950 }}>No attack paths match your filters</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Try lowering the minimum score or clearing search.
                          </Typography>
                        </Paper>
                      )}
                    </Stack>
                  </CardContent>
                )}

                {tab === 1 && (
                  <CardContent>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        p: 2,
                        bgcolor: alpha(PRIMARYCOLOR, 0.04),
                        borderColor: alpha(PRIMARYCOLOR, 0.16),
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: 950 }}>Model Narrative</Typography>
                        <Button size="small" startIcon={<OpenInFullIcon />} onClick={() => setRawOpen(true)}>
                          Fullscreen narrative
                        </Button>
                      </Stack>

                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                        {active?.md || '_No threat model available._'}
                      </ReactMarkdown>
                    </Paper>
                  </CardContent>
                )}
              </Card>

              <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 1, border: `1px solid ${alpha(PRIMARYCOLOR, 0.1)}` }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 950, mb: 0.5 }}>How to use this view</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select an endpoint on the right, review the ranked attack paths, then open a path to see why it applies and whether any findings were confirmed.
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}
        </Box>

        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64,
            height: 'calc(100vh - 64px)',
            flex: '0 0 360px',
            maxWidth: 360,
            minWidth: 360,
            borderLeft: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
            bgcolor: '#ffffff',
            overflowY: 'auto',
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1 }}>
            <Alert
              severity="info"
              icon={<ChevronRightIcon />}
              sx={{
                mb: 1.25,
                borderRadius: 3,
                bgcolor: alpha(PRIMARYCOLOR, 0.05),
                border: `1px dashed ${alpha(PRIMARYCOLOR, 0.22)}`,
              }}
            >
              <Typography sx={{ fontWeight: 950 }}>
                {activeIdx === 0 ? 'Select an endpoint to review attack paths' : 'Select another endpoint'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Each route shows its method, top score, likely paths, findings count, and modeled input surface.
              </Typography>
            </Alert>

            <ToggleButtonGroup size="small" value={assetSort} exclusive onChange={(_, value) => value && setAssetSort(value)} fullWidth sx={{ mb: 1.25 }}>
              <ToggleButton value="RISK" sx={{ fontWeight: 900 }}>
                Highest risk
              </ToggleButton>
              <ToggleButton value="NAME" sx={{ fontWeight: 900 }}>
                Name
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              fullWidth
              placeholder="Search endpoints..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setKbdIndex(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.25 }}>
              <Chip
                size="small"
                label="ALL"
                clickable
                color={methodFilter === 'ALL' ? 'primary' : 'default'}
                onClick={() => setMethodFilter('ALL')}
                sx={{ fontWeight: 900 }}
              />
              {METHOD_ORDER.map((method) => (
                <Chip
                  key={method}
                  size="small"
                  label={method}
                  clickable
                  onClick={() => setMethodFilter(method)}
                  sx={{
                    fontWeight: 900,
                    bgcolor: methodFilter === method ? methodColor(method) : '#fff',
                    color: methodFilter === method ? methodTextColor(method) : 'inherit',
                    border: `1px solid ${alpha(methodTextColor(method), methodFilter === method ? 0.15 : 0.08)}`,
                  }}
                />
              ))}
            </Stack>
          </Box>

          <List
            dense
            onKeyDown={onKeyDownList}
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: '#fff', fontWeight: 950, fontSize: '1rem' }}>
                Endpoints
              </ListSubheader>
            }
          >
            {filteredEndpoints.map((endpoint) => {
              const isSelected = endpoints[activeIdx]?.id === endpoint.id;

              if (endpoint.id === 0) {
                return (
                  <Tooltip key={endpoint.id} title="Overview dashboard" placement="left" arrow enterDelay={300}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => setActiveIdx(0)}
                      sx={{
                        alignItems: 'flex-start',
                        py: 1.25,
                        borderLeft: isSelected ? `3px solid ${PRIMARYCOLOR}` : '3px solid transparent',
                        '&.Mui-selected': { bgcolor: lighten(PRIMARYCOLOR, 0.9) },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip size="small" label="OVERVIEW" variant="outlined" />
                            <Typography variant="body2" sx={{ fontWeight: isSelected ? 950 : 600 }}>
                              Web Threat Overview
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Portfolio view and quick picks
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </ListItemButton>
                  </Tooltip>
                );
              }

              const meta = endpointMeta[endpoint.path] || {};
              const likely = meta.likelyCount || 0;
              const findings = meta.findingsCount || 0;

              return (
                <Tooltip key={`${endpoint.method}-${endpoint.path}`} title={`${endpoint.method} ${endpoint.path}`} placement="left" arrow enterDelay={300}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setActiveIdx(endpoints.findIndex((item) => item.id === endpoint.id))}
                    sx={{
                      alignItems: 'flex-start',
                      py: 1.25,
                      borderLeft: isSelected ? `3px solid ${PRIMARYCOLOR}` : '3px solid transparent',
                      '&.Mui-selected': { bgcolor: lighten(PRIMARYCOLOR, 0.9) },
                      '&:hover': { bgcolor: alpha(PRIMARYCOLOR, 0.04) },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Chip
                                size="small"
                                label={endpoint.method}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: methodColor(endpoint.method),
                                  color: methodTextColor(endpoint.method),
                                }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 950 : 700, wordBreak: 'break-word' }}>
                                {trunc(endpoint.displayPath || endpoint.path, 52)}
                              </Typography>
                            </Stack>
                            <Chip
                              size="small"
                              label={Math.round(meta.maxScore || 0)}
                              sx={{
                                fontWeight: 950,
                                bgcolor: alpha(scoreColor(meta.maxScore || 0), 0.1),
                                color: scoreColor(meta.maxScore || 0),
                              }}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {meta.totalAttacks || 0} paths modeled, {likely} likely, {findings} findings
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                          <Chip size="small" label={`Inputs: ${meta.inputCount || 0}`} sx={{ bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
                          <Chip size="small" label={`Findings: ${findings}`} sx={{ bgcolor: alpha('#D32F2F', 0.08), color: findings > 0 ? '#D32F2F' : 'inherit', fontWeight: 900 }} />
                          <Chip variant="outlined" size="small" label="Open review" icon={<ChevronRightIcon />} sx={{ borderStyle: 'dashed' }} />
                        </Box>
                      }
                      sx={{ m: 0 }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={!!selectedAttack}
        onClose={() => setSelectedAttack(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 2,
            borderLeft: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            Attack analysis
          </Typography>
          <IconButton onClick={() => setSelectedAttack(null)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {selectedAttack &&
          (() => {
            const score = num(selectedAttack?.score);
            const findings = getFindings(selectedAttack);
            const findingCount = findings.length;
            const confirmed = selectedAttack?.exploit_results?.vulnerable === true || findingCount > 0;
            const activeMeta = endpointMeta[active?.path] || {};
            const attackSeverity = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };

            findings.forEach((finding) => {
              attackSeverity[sevKey(finding?.severity)] += 1;
            });

            return (
              <>
                <Stack spacing={0.75} sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 950 }}>{selectedAttack?.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review why this path applies to{' '}
                    <b>
                      {active?.method} {active?.displayPath || active?.path}
                    </b>{' '}
                    and whether validation produced confirmed findings.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`${active?.method} ${trunc(active?.displayPath || 'Unknown endpoint', 34)}`}
                      sx={{ fontWeight: 900 }}
                    />
                    <Chip
                      size="small"
                      label={willVerify(score) ? 'Ready for verification' : 'Below verification threshold'}
                      sx={{
                        fontWeight: 900,
                        bgcolor: alpha(willVerify(score) ? '#EF6C00' : PRIMARYCOLOR, 0.08),
                        color: willVerify(score) ? '#EF6C00' : PRIMARYCOLOR,
                      }}
                    />
                  </Stack>
                </Stack>

                <Grid container spacing={1.25} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: alpha(scoreColor(score), 0.18) }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        SCORE
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: scoreColor(score) }}>
                        {score}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scoreLabel(score)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: alpha('#EF6C00', 0.18) }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        STATUS
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 950, mt: 0.5 }}>
                        {willVerify(score) ? 'Queued for validation' : 'Analyst review'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Threshold: {VERIFY_THRESHOLD}+
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: alpha('#D32F2F', 0.18) }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        FINDINGS
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: findingCount > 0 ? '#D32F2F' : 'inherit' }}>
                        {findingCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {confirmed ? 'Confirmed evidence recorded' : 'No confirmed evidence yet'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(PRIMARYCOLOR, 0.16) }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Why this path applies
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedAttack?.reason || 'No reason provided.'}
                  </Typography>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    mt: 2,
                    borderColor: alpha(PRIMARYCOLOR, 0.16),
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Observed endpoint surface
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip size="small" label={`Method: ${active?.method}`} variant="outlined" />
                    <Chip size="small" label={`Query keys: ${activeMeta.queryKeys?.length || 0}`} variant="outlined" />
                    <Chip size="small" label={`Path params: ${activeMeta.pathParams?.length || 0}`} variant="outlined" />
                    {activeMeta.stateChanging && <Chip size="small" label="State-changing route" variant="outlined" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-word' }}>
                    {active?.displayPath || active?.path}
                  </Typography>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    mt: 2,
                    borderStyle: 'dashed',
                    borderColor: alpha(PRIMARYCOLOR, 0.18),
                    bgcolor: alpha(PRIMARYCOLOR, 0.03),
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Confirmed findings
                  </Typography>

                  {findingCount === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No findings recorded for this attack yet.
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ mt: 1 }}>
                      {findings.map((finding, index) => {
                        const name = finding?.name || finding?.title || finding?.finding || `Finding ${index + 1}`;
                        const severityKey = sevKey(finding?.severity);
                        const severity = String(finding?.severity || 'info');

                        return (
                          <ListItemText
                            key={`${name}-${index}`}
                            primary={
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 900, pr: 1 }}>
                                  {name}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={severity.toUpperCase()}
                                  sx={{
                                    fontWeight: 950,
                                    bgcolor: alpha(sevColor(severityKey), 0.1),
                                    color: sevColor(severityKey),
                                    border: `1px solid ${alpha(sevColor(severityKey), 0.18)}`,
                                  }}
                                />
                              </Stack>
                            }
                            sx={{ py: 0.75, borderBottom: `1px solid ${alpha('#000', 0.06)}` }}
                          />
                        );
                      })}
                    </List>
                  )}

                  <Divider sx={{ my: 1.25 }} />

                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Findings breakdown
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {['critical', 'high', 'medium', 'low', 'info'].map((key) => (
                      <Chip
                        key={key}
                        size="small"
                        label={`${key.toUpperCase()} - ${attackSeverity[key]}`}
                        sx={{
                          fontWeight: 950,
                          bgcolor: alpha(sevColor(key), 0.1),
                          color: sevColor(key),
                          border: `1px solid ${alpha(sevColor(key), 0.18)}`,
                        }}
                      />
                    ))}
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                    Context:{' '}
                    <b>
                      {active?.method} {active?.displayPath || active?.path}
                    </b>
                  </Typography>
                </Paper>
              </>
            );
          })()}
      </Drawer>

      <Dialog open={rawOpen} onClose={() => setRawOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 950 }}>Model Narrative</DialogTitle>
        <DialogContent dividers>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 3,
              bgcolor: alpha(PRIMARYCOLOR, 0.06),
              border: `1px solid ${alpha(PRIMARYCOLOR, 0.18)}`,
            }}
          >
            This is the agent's Threat Model output. It may include speculative hypotheses and should not be treated as verified vulnerabilities.
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: 2,
              bgcolor: '#0B1220',
              color: '#E8F0FF',
              borderColor: alpha(PRIMARYCOLOR, 0.25),
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponentsTerminal}>
              {active?.md || '_No threat model available._'}
            </ReactMarkdown>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PentestWebThreatModel;
